import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OfficeNotesIcon from '../../assets/icons/practice-office-note-icon.svg';
import { useCustomFields } from '../../hooks/useCustomFields';
import { usePermissions } from '../../hooks/usePermissions';
import {
  generateAlertLink,
  getUserInfoFromToken,
  notifyCHSOnSubmission,
  notifyDenimOnApproval,
  notifyDenimOnSave,
} from '../../services/notifications/transferOutcomesNotifications';
import { updatePracticeById } from '../../services/practices/updatePracticeById';
import { updateProviderById } from '../../services/providers/updateProviderById';
import { CustomFieldValue, Practice, Provider } from '../../types';
import { CustomField } from '../../types/custom-fields/types';
import {
  CommentLogEntry,
  createCommentLogEntry,
  createDefaultTransferOutcomes,
  getCommentLog,
  getTransferOutcomes,
  TransferOutcomesData,
  TransferOutcomesMetadata,
} from '../../types/transferOutcomes';
import { analytics } from '../../utils/analytics';
import { CATEGORY_LEGEND } from '../../utils/categoryLegend';
import {
  findOfficeNotesField,
  getClientIdWithFallback,
} from '../../utils/customFieldUtils';
import {
  clearProcessedAlertId,
  isAlertIdProcessed,
  markAlertIdAsProcessed,
} from '../../utils/deepLinkCoordinator';
import { getVisibleText } from '../../utils/textConversion';
import NotePreview from '../note-preview/NotePreview';
import RichTextEditor from '../rich-text-editor/RichTextEditor';
import TransferOutcomesConfig from '../transfer-outcomes/TransferOutcomesConfig';

interface OfficeNotesCardProps {
  width?: string;
  practice?: Practice;
  provider?: Provider;
  isLoading?: boolean;
  customFields?: CustomField[]; // Add customFields prop
  practices?: Array<{ id: string; name: string }>; // Add practices prop for fallback
  isPracticesLoading?: boolean; // Add loading state for practices
  specialtyOptions?: { label: string; value: string }[]; // Specialty options from parent
  isSpecialtyOptionsLoading?: boolean; // Loading state for specialty options
  isCustomFieldsLoading?: boolean; // Add loading state for custom fields
  customFieldsError?: string | null; // Add error state for custom fields
}

type FieldIdLookup = {
  fieldId?: string;
  field_id?: string;
  customFieldId?: string;
  custom_field_id?: string;
};

// Category color mapping
const getCategoryColor = (category: string): string => {
  const colorMap: Record<string, string> = {
    ADMIN_INFO: 'gray',
    CLINIC_CLOSED: 'red',
    COVID_POLICY: 'orange',
    INMATE_TRANSFER: 'purple',
    INSURANCE_NOTICE: 'blue',
    LIMITED_AVAILABILITY: 'yellow',
    NOT_ON_PAC_PRACTICE: 'pink',
    NOT_ON_PAC_PROVIDER: 'pink',
    NOT_ON_PAC_SPECIALTY_SERVICE: 'pink',
    OOO: 'red',
    PRACTICE_LOCATION_CHANGE: 'cyan',
    PROVIDER_LOCATION_CHANGE: 'cyan',
    PROVIDER_START_END: 'teal',
    SCHEDULING_RULE: 'cyan',
    SERVICE_SCOPE: 'purple',
    UTILITY_ISSUES: 'orange',
    VIRTUAL_WAITING_ROOM: 'yellow',
  };
  return colorMap[category] || 'gray';
};

const OfficeNotesCard: React.FC<OfficeNotesCardProps> = ({
  practice,
  provider,
  width,
  customFields: passedCustomFields,
  practices,
  isPracticesLoading = false,
  specialtyOptions = [],
  isSpecialtyOptionsLoading = false,
  isCustomFieldsLoading: passedCustomFieldsLoading = false,
  customFieldsError: passedCustomFieldsError = null,
}) => {
  // Helper to get practice name by id from provider.relatedPractices or practices prop
  const getPracticeNameById = (id: string): string => {
    const practiceFromProvider = provider?.relatedPractices?.find(
      (p) => p.id === id
    );
    if (practiceFromProvider) {
      return practiceFromProvider.name;
    }

    const practiceFromProps = practices?.find((p) => p.id === id);
    if (practiceFromProps) {
      return practiceFromProps.name;
    }

    return 'Unknown Practice';
  };

  // Helper to extract practice ID from relatedPracticeId metadata
  const extractPracticeId = (
    relatedPracticeId:
      | string
      | string[]
      | { providerId: string }
      | { providerId: string; providerLabel: string }[]
      | null
      | undefined
  ): string | null => {
    if (typeof relatedPracticeId === 'string') {
      return relatedPracticeId;
    } else if (Array.isArray(relatedPracticeId)) {
      const firstItem = relatedPracticeId[0];
      if (typeof firstItem === 'string') {
        return firstItem;
      } else if (
        firstItem &&
        typeof firstItem === 'object' &&
        'providerId' in firstItem
      ) {
        return firstItem.providerId;
      }
      return null;
    } else if (
      relatedPracticeId &&
      typeof relatedPracticeId === 'object' &&
      'providerId' in relatedPracticeId
    ) {
      return relatedPracticeId.providerId;
    }
    return null;
  };

  const [notes, setNotes] = useState<CustomFieldValue[]>([]);
  const [editingNote, setEditingNote] =
    useState<CustomFieldValue | null>(null);
  const [tempNote, setTempNote] = useState<string>('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [noteToDelete, setNoteToDelete] =
    useState<CustomFieldValue | null>(null);
  const [neverExpire, setNeverExpire] = useState<boolean>(false);
  const [associateWithPractice, setAssociateWithPractice] =
    useState<boolean>(false);
  const [selectedPracticeId, setSelectedPracticeId] =
    useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<
    string[]
  >([]);

  const resolveNoteFieldId = (
    note?: CustomFieldValue | null,
    fallback?: string | null
  ): string | null => {
    const carrier = note as FieldIdLookup | null | undefined;
    return (
      carrier?.fieldId ??
      carrier?.field_id ??
      carrier?.customFieldId ??
      carrier?.custom_field_id ??
      fallback ??
      null
    );
  };

  // Transfer outcomes state
  const [transferOutcomes, setTransferOutcomes] =
    useState<TransferOutcomesData>(createDefaultTransferOutcomes());
  const [comment, setComment] = useState<string>('');
  const [commentLog, setCommentLog] = useState<CommentLogEntry[]>([]);
  const [isSubmittingForApproval, setIsSubmittingForApproval] =
    useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isAddingComment, setIsAddingComment] =
    useState<boolean>(false);
  const [localSpecialtyLoading, setLocalSpecialtyLoading] =
    useState<boolean>(false);
  const hasSetLoadingForThisModalOpen = useRef<boolean>(false);

  // Get user permissions
  const {
    canEditDailyNotes,
    canEditOfficeAlerts,
    isAdmin,
    isReady: permissionsReady,
  } = usePermissions();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isCategoryLegendOpen,
    onClose: onCategoryLegendClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to get entity name (practice or provider)
  const getEntityName = (): string => {
    if (practice) {
      return practice.name;
    }
    if (provider) {
      const firstName =
        provider.firstName || provider.firstname || '';
      const lastName = provider.lastName || provider.lastname || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Provider';
    }
    return 'Unknown Entity';
  };
  // Normalize and dedupe by id
  const normalizeNotes = (
    items: CustomFieldValue[] | undefined | null
  ) => {
    const unique: Record<string, CustomFieldValue> = {};
    (items || []).forEach((item) => {
      if (item && item.id) {
        if (!unique[item.id]) unique[item.id] = item;
      }
    });
    return Object.values(unique);
  };

  // Use passed customFields if available, otherwise fetch them
  const clientId = practice?.clientId || getClientIdWithFallback();
  const entityType = practice ? 'Practice' : 'ProviderData';

  // Use custom hook to fetch custom fields ONLY if not passed as prop
  const {
    customFields: fetchedCustomFields,
    loading: isFieldIdLoading,
    error: customFieldsError,
    refresh: refreshCustomFields,
  } = useCustomFields(
    passedCustomFields ? null : clientId, // Don't fetch if customFields are passed
    passedCustomFields ? null : entityType
  );

  // Normalize helper for arrays or map-shaped custom fields
  const normalizeCustomFieldsToArray = (
    fields:
      | CustomField[]
      | Record<string, CustomField>
      | Record<string, unknown>
      | undefined
      | null
  ): CustomField[] | undefined => {
    if (!fields) return undefined;
    if (Array.isArray(fields)) return fields;
    return Object.values(fields) as CustomField[];
  };

  const providerCustomFieldsSnake =
    (provider as { custom_fields?: Record<string, unknown> } | undefined)
      ?.custom_fields;
  const requestedFieldRefreshRef = useRef<boolean>(false);
  const latestEffectiveCustomFieldsRef = useRef<CustomField[]>([]);
  const latestOfficeNotesFieldIdRef = useRef<string | null>(null);

  // Prefer passed/fetched custom fields; fall back to entity payload
  const effectiveCustomFields = React.useMemo<CustomField[]>(() => {
    const fromPassed = normalizeCustomFieldsToArray(passedCustomFields);
    if (fromPassed?.length) return fromPassed;

    const fromFetched = normalizeCustomFieldsToArray(fetchedCustomFields);
    if (fromFetched?.length) return fromFetched;

    const fromPracticeCamel = normalizeCustomFieldsToArray(
      practice?.customFields as unknown as Record<string, unknown>
    );
    if (fromPracticeCamel?.length) return fromPracticeCamel;

    const fromPracticeSnake = normalizeCustomFieldsToArray(
      practice?.custom_fields as unknown as Record<string, unknown>
    );
    if (fromPracticeSnake?.length) return fromPracticeSnake;

    const fromProviderCamel = normalizeCustomFieldsToArray(
      provider?.customFields as unknown as Record<string, unknown>
    );
    if (fromProviderCamel?.length) return fromProviderCamel;

    const fromProviderSnake = normalizeCustomFieldsToArray(
      providerCustomFieldsSnake
    );
    if (fromProviderSnake?.length) return fromProviderSnake;

    return [];
  }, [
    passedCustomFields,
    fetchedCustomFields,
    practice?.customFields,
    practice?.custom_fields,
    provider,
    providerCustomFieldsSnake,
  ]);

  const officeNotesFieldId = React.useMemo<string | null>(() => {
    const field = findOfficeNotesField(effectiveCustomFields);
    return field?.id ?? null;
  }, [effectiveCustomFields]);

  // Get Notes-Category field for multiselect (commented out - category selection UI is disabled)
  // const notesCategoryField = customFields.find(
  //   (field) => field.name === 'Notes-Category' || field.field_type === 'Notes-Category'
  // );

  // const categoryOptions = notesCategoryField?.allowed_values
  //   ? [...notesCategoryField.allowed_values]
  //       .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  //       .map((val) => {
  //         const friendlyName =
  //           CATEGORY_LEGEND[val.value as keyof typeof CATEGORY_LEGEND]
  //             ?.name;
  //         const fallbackName = val.value
  //           .replace(/_/g, ' ')
  //           .replace(/\b\w/g, (l) => l.toUpperCase());

  //         // Debug logging to see what's happening
  //         if (!friendlyName) {
  //           console.log(
  //             'Category not found in legend:',
  //             val.value,
  //             'using fallback:',
  //             fallbackName
  //           );
  //         }

  //         return {
  //           label: friendlyName || fallbackName,
  //           value: val.value,
  //         };
  //       })
  //   : [];

  // Track when modal opens and handle specialty options loading
  useEffect(() => {
    // Reset ref when modal closes
    if (!isOpen) {
      hasSetLoadingForThisModalOpen.current = false;
      setLocalSpecialtyLoading(false);
      return;
    }

    // Modal is open
    if (specialtyOptions.length > 0) {
      // Options are available, clear local loading
      setLocalSpecialtyLoading(false);
      hasSetLoadingForThisModalOpen.current = false;
      return;
    }

    // Modal is open but options are empty
    // Keep loading true if parent is still loading
    if (isSpecialtyOptionsLoading) {
      setLocalSpecialtyLoading(true);
      hasSetLoadingForThisModalOpen.current = true;
      return;
    }

    // Parent finished loading but options are still empty
    // Only set loading once per modal open session, then wait a bit
    if (!hasSetLoadingForThisModalOpen.current) {
      hasSetLoadingForThisModalOpen.current = true;
      setLocalSpecialtyLoading(true);
      // Wait a bit for options to load after parent loading completes
      const timeout = setTimeout(() => {
        setLocalSpecialtyLoading(false);
      }, 3000); // Give 3 seconds after modal opens for options to load
      return () => clearTimeout(timeout);
    }
  }, [isOpen, specialtyOptions.length, isSpecialtyOptionsLoading]);

  // Keep latest field id in sync with resolved Office-Notes field
  useEffect(() => {
    latestOfficeNotesFieldIdRef.current = officeNotesFieldId;
  }, [officeNotesFieldId]);

  // Track latest effective custom fields for async refetch flows
  useEffect(() => {
    latestEffectiveCustomFieldsRef.current = effectiveCustomFields;
  }, [effectiveCustomFields]);

  // If modal is open and field id is still missing, trigger one refresh of custom fields
  useEffect(() => {
    if (
      isOpen &&
      !officeNotesFieldId &&
      !isFieldIdLoading &&
      !requestedFieldRefreshRef.current
    ) {
      requestedFieldRefreshRef.current = true;
      refreshCustomFields?.();
    }
  }, [isOpen, officeNotesFieldId, isFieldIdLoading, refreshCustomFields]);

  const ensureOfficeNotesFieldId = useCallback(
    async (note?: CustomFieldValue | null): Promise<string | null> => {
      const currentId = resolveNoteFieldId(
        note,
        latestOfficeNotesFieldIdRef.current
      );
      if (currentId) return currentId;

      await refreshCustomFields?.();
      // Give state a brief tick to update
      await new Promise((res) => setTimeout(res, 50));

      const refreshedId = resolveNoteFieldId(
        note,
        findOfficeNotesField(latestEffectiveCustomFieldsRef.current)?.id ??
          null
      );
      return refreshedId ?? null;
    },
    [refreshCustomFields]
  );

  // Handle case where custom fields failed to load
  // Note: With caching, this is less critical but we still log for debugging
  useEffect(() => {
    if (customFieldsError && !isFieldIdLoading) {
      console.warn(
        'Custom fields failed to load for OfficeNotesCard:',
        customFieldsError
      );
    }
  }, [customFieldsError, isFieldIdLoading]);

  useEffect(() => {
    // Use provider data directly from props instead of making individual API calls
    if (provider) {
      const normalized = normalizeNotes(
        provider?.customFields?.['Office-Notes']
      );
      setNotes(normalized);
    } else if (practice) {
      const normalized = normalizeNotes(
        practice?.customFields?.['Office-Notes']
      );
      setNotes(normalized);
    }
  }, [practice, provider]);

  // When editing, set neverExpire if endDate is null
  const handleEditNote = useCallback(
    (note: CustomFieldValue) => {
      setEditingNote(note);
      setTempNote(note.value);
      setStartDate(note.startDate);
      setEndDate(note.endDate);
      setNeverExpire(note.endDate === null);

      // Handle relatedPracticeId from metadata - extract the actual practice ID
      let practiceId = '';
      if (note.metadata?.relatedPracticeId) {
        const idRaw = note.metadata.relatedPracticeId;
        if (Array.isArray(idRaw)) {
          const firstItem = idRaw[0];
          if (typeof firstItem === 'string') {
            practiceId = firstItem;
          } else if (
            typeof firstItem === 'object' &&
            'providerId' in firstItem
          ) {
            practiceId = (firstItem as { providerId: string })
              .providerId;
          }
        } else if (
          typeof idRaw === 'object' &&
          'providerId' in idRaw
        ) {
          practiceId = (idRaw as { providerId: string }).providerId;
        } else if (typeof idRaw === 'string') {
          practiceId = idRaw;
        }
      }

      setAssociateWithPractice(!!practiceId);
      setSelectedPracticeId(practiceId);

      // Load categories from metadata
      const categories = note.metadata?.categories;
      if (Array.isArray(categories)) {
        // Filter to only include string values
        const stringCategories = categories.filter(
          (cat): cat is string => typeof cat === 'string'
        );
        setSelectedCategories(stringCategories);
      } else {
        setSelectedCategories([]);
      }

      // Load transfer outcomes from metadata
      const loadedTransferOutcomes = getTransferOutcomes(
        note.metadata
      );
      if (loadedTransferOutcomes) {
        setTransferOutcomes(loadedTransferOutcomes);
      } else {
        // Default to transfer for all vectors
        setTransferOutcomes(createDefaultTransferOutcomes());
      }

      // Load comment log from metadata
      const loadedCommentLog = getCommentLog(note.metadata);
      setCommentLog(loadedCommentLog);
      // Clear current comment when editing
      setComment('');

      onOpen();
    },
    [onOpen]
  );

  // Deep linking support: Check for alertId in URL query params
  // Backend expects alertId for both alerts and notes
  // Use shared coordinator - OfficeNotesCard only processes if DailyAlertsCard hasn't already
  useEffect(() => {
    const alertIdFromUrl = searchParams.get('alertId');

    // Reset coordinator state if alertId is removed from URL
    if (!alertIdFromUrl) {
      clearProcessedAlertId(alertIdFromUrl);
      return;
    }

    // Skip if already processed by DailyAlertsCard or another instance
    // DailyAlertsCard is the primary processor (checks both alerts and notes)
    if (isAlertIdProcessed(alertIdFromUrl)) {
      return;
    }

    // We need an entity to process the deep link
    const hasEntity = practice || provider;
    if (!hasEntity) {
      // No entity yet, wait for next render
      return;
    }

    // Wait for permissions to be ready before processing
    // This prevents false "Access Denied" toasts when auth is still initializing
    if (!permissionsReady) {
      // Permissions not ready yet, wait for next render
      return;
    }

    // Check if customFields are available
    const hasCustomFields =
      practice?.customFields !== undefined ||
      provider?.customFields !== undefined;

    // If customFields not available yet, wait a bit for data to load
    // Note: OfficeNotesCard doesn't set timeout - DailyAlertsCard handles that
    if (!hasCustomFields) {
      // Just wait for next render when data loads
      return;
    }

    // Get notes directly from props to ensure we check against latest data
    const rawNotes = provider
      ? provider?.customFields?.['Office-Notes']
      : practice?.customFields?.['Office-Notes'];

    const currentNotes = normalizeNotes(rawNotes);

    // Find the note by ID
    const noteToOpen = currentNotes.find(
      (note) => note.id === alertIdFromUrl
    );

    if (noteToOpen) {
      // Found a note - mark as processed and handle it
      // This prevents DailyAlertsCard from also processing it
      markAlertIdAsProcessed(alertIdFromUrl);

      // Check RBAC permissions
      const hasPermission =
        canEditDailyNotes() || canEditOfficeAlerts() || isAdmin();

      if (hasPermission) {
        // Open edit modal with the note
        handleEditNote(noteToOpen);
        // Clean up query param
        searchParams.delete('alertId');
        setSearchParams(searchParams, { replace: true });
      } else {
        // Show error toast and redirect
        toast({
          title: 'Access Denied',
          description:
            "You don't have access to view or edit alerts/notes. Only admins and alert editors can access them.",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Clean up query param
        searchParams.delete('alertId');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // If note not found, DON'T mark as processed - let DailyAlertsCard handle showing the toast

    // No cleanup needed - coordinator handles timeouts
  }, [
    searchParams,
    practice,
    provider,
    permissionsReady,
    canEditDailyNotes,
    canEditOfficeAlerts,
    isAdmin,
    handleEditNote,
    toast,
    setSearchParams,
  ]);

  // When adding new, reset neverExpire
  const handleAddNewNote = useCallback(() => {
    setEditingNote(null);
    setTempNote('');
    setStartDate(null);
    setEndDate(null);
    setNeverExpire(false);
    setAssociateWithPractice(false);
    setSelectedPracticeId('');
    setSelectedCategories([]);
    // Reset transfer outcomes to defaults (transfer for all vectors)
    setTransferOutcomes(createDefaultTransferOutcomes());
    setComment('');
    setCommentLog([]);
    onOpen();
  }, [onOpen]);

  // Save logic: if neverExpire, set endDate as null
  const handleSaveNote = async () => {
    // Validation: Note text cannot be empty (check visible text content, not just HTML)
    const visibleText = getVisibleText(tempNote);
    if (!visibleText || visibleText.trim().length === 0) {
      toast({
        title: 'Note text is required.',
        description: 'Please enter the note message before saving.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Start date cannot be empty
    if (!startDate) {
      toast({
        title: 'Start Date is required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: End date cannot be empty unless "Never Expire" is checked
    if (!neverExpire && !endDate) {
      toast({
        title:
          'End Date is required unless "Never Expire" is selected.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Date logic and user feedback
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    // Keep variable for potential future checks; avoids unused var lint by simple reference
    const startDateObj = new Date(startDate!);
    void startDateObj;

    if (!neverExpire && endDate) {
      const endDateObj = new Date(endDate);

      // Check if end date is in the past (only for new items)
      if (!editingNote && endDateObj < today) {
        toast({
          title: 'Invalid End Date',
          description:
            'End date cannot be in the past for new notes.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Enforce End Date on or after Start Date (allow same-day notes)
      const startVsEndStart = new Date(startDate);
      if (endDateObj < startVsEndStart) {
        toast({
          title: 'Invalid Date Range',
          description: 'End Date must be on or after the Start Date.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      // Allow endDate to be today, including start=end=today

      // Note: Start dates can be in the past, only end dates are validated
    }

    // Resolve fieldId; if missing, force a refresh before failing
    const noteFieldId = await ensureOfficeNotesFieldId(editingNote);
    if (!noteFieldId) {
      console.error('Field ID is missing. Cannot save note.', {
        editingNote: editingNote?.id,
        noteFieldId: editingNote?.fieldId,
        resolvedFieldId: officeNotesFieldId,
      });
      toast({
        title: 'Field ID is missing. Cannot save note.',
        description: 'Please refresh the page and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Prepare transfer outcomes with alwaysTransfer set to true
    const transferOutcomesToSave = {
      ...transferOutcomes,
      alwaysTransfer: true, // Set to true when "Save Note" is clicked
    };

    const entityId = provider ? provider.id : practice?.id || '';
    const entityType = provider ? 'provider' : 'practice';

    const updatedCommentLogEntries = (() => {
      const userInfo = getUserInfoFromToken();
      if (!userInfo) {
        return commentLog;
      }
      const action = editingNote ? 'updated' : 'created';
      const newEntry = createCommentLogEntry(action, {
        userId: userInfo.id,
        userName: userInfo.name,
        userEmail: userInfo.email,
        permissionGroup: userInfo.permissionGroup,
      });
      return [...commentLog, newEntry];
    })();

    const existingMetadata = {
      ...(editingNote?.metadata || {}),
    } as Record<string, unknown>;

    const sanitizedMetadata: Record<string, unknown> = {
      ...existingMetadata,
      htmlContent: tempNote,
      categories: selectedCategories,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transferOutcomes: transferOutcomesToSave as any,
      commentLog: updatedCommentLogEntries,
    };

    if (neverExpire) {
      sanitizedMetadata.neverExpire = 'true';
    } else {
      delete sanitizedMetadata.neverExpire;
    }

    if (
      associateWithPractice &&
      selectedPracticeId &&
      selectedPracticeId.trim() !== ''
    ) {
      sanitizedMetadata.relatedPracticeId = selectedPracticeId;
      sanitizedMetadata.relatedPracticeName =
        provider?.relatedPractices?.find(
          (practice) => practice.id === selectedPracticeId
        )?.name ||
        practices?.find(
          (practice) => practice.id === selectedPracticeId
        )?.name ||
        'Unknown Practice';
    } else {
      delete sanitizedMetadata.relatedPracticeId;
      delete sanitizedMetadata.relatedPracticeName;
    }

    delete sanitizedMetadata.approvalStatus;
    delete sanitizedMetadata.approvedAt;
    delete sanitizedMetadata.approvedBy;
    delete sanitizedMetadata.submittedForApproval;
    delete sanitizedMetadata.submittedAt;

    const newNote: CustomFieldValue = {
      fieldId: noteFieldId,
      rowId: entityId,
      value: tempNote,
      startDate,
      endDate: neverExpire ? null : endDate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metadata: sanitizedMetadata as any,
      id: editingNote?.id ?? undefined,
    };

    setCommentLog(updatedCommentLogEntries);

    const updatedNotes = editingNote
      ? notes.map((note) =>
          note.id === editingNote.id ? { ...note, ...newNote } : note
        )
      : [...notes, newNote];

    // Update local state immediately for instant UI feedback
    setNotes(updatedNotes);

    if (editingNote) {
      setEditingNote({ ...editingNote, ...newNote });
    }

    try {
      // Save notes and get updated notes with IDs from backend
      const savedNotesWithIds = await saveNotes(updatedNotes);
      analytics.trackEvent(editingNote ? 'update' : 'create', 'office_alert');

      // Find the saved note by matching value and startDate (for new notes) or by ID (for existing notes)
      const savedNote = savedNotesWithIds.find(
        (note) =>
          (editingNote?.id && note.id === editingNote.id) ||
          (!editingNote?.id &&
            note.value === tempNote &&
            note.startDate === startDate)
      );
      const noteIdAfterSave = savedNote?.id || editingNote?.id;

      // Send notification to Denim users
      // Backend expects alertId for both alerts and notes
      try {
        await notifyDenimOnSave({
          alertId: noteIdAfterSave,
          entityType,
          entityId,
          link: generateAlertLink(
            entityType,
            entityId,
            noteIdAfterSave
          ),
          entityName: getEntityName(),
        });
      } catch (error) {
        console.error('Failed to send notification:', error);
        // Don't block the save operation
      }

      // Clear comment after save
      setComment('');

      // Close modal after successful save
      onClose();
    } catch (error) {
      // Error is already handled in saveNotes, but ensure modal stays open
      console.error('Failed to save note:', error);
      // Modal will remain open so user can fix errors and retry
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      toast({
        title: 'Comment Required',
        description:
          'Please enter a comment before adding it to the log.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!editingNote?.id) {
      toast({
        title: 'Save Required',
        description:
          'Please save the note first before adding comments.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!editingNote) {
      toast({
        title: 'Error',
        description: 'No note selected. Please save the note first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAddingComment(true);

    try {
      const userInfo = getUserInfoFromToken();
      if (!userInfo) {
        toast({
          title: 'Error',
          description:
            'User information is missing. Please refresh and try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Add comment entry to comment log
      const newCommentEntry = createCommentLogEntry(
        'comment_added',
        {
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          permissionGroup: userInfo.permissionGroup,
        },
        comment.trim()
      );

      const updatedCommentLog = [...commentLog, newCommentEntry];

      // Update the note with new comment log
      const updatedNote: CustomFieldValue = {
        ...editingNote,
        metadata: {
          ...editingNote.metadata,
          commentLog: updatedCommentLog,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      };

      const updatedNotes = notes.map((note) =>
        note.id === editingNote.id ? updatedNote : note
      );

      // Save the updated note
      await saveNotes(updatedNotes, false);

      // Update local state
      setCommentLog(updatedCommentLog);
      setComment(''); // Clear comment after adding

      toast({
        title: 'Comment Added Successfully',
        description: 'Your comment has been added to the audit log.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast({
        title: 'Failed to Add Comment',
        description:
          'An error occurred while adding the comment. Please check your connection and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!editingNote?.id) {
      toast({
        title: 'Save Required First',
        description:
          'Please save the note first before submitting for approval. The note must exist before it can be submitted.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!comment.trim()) {
      toast({
        title: 'Comment Required',
        description:
          'Please enter a comment explaining the transfer outcomes before submitting for approval. This helps CHS reviewers understand your decisions.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmittingForApproval(true);

    try {
      const entityId = provider ? provider.id : practice?.id || '';
      const entityType = provider ? 'provider' : 'practice';

      // Update the note with pending approval status
      if (editingNote) {
        // Use fieldId from the note itself; if missing, try refresh before failing
        const noteFieldId = await ensureOfficeNotesFieldId(editingNote);
        if (!noteFieldId) {
          console.error(
            'Field ID is missing. Cannot submit for approval.',
            {
              editingNote: editingNote.id,
              noteFieldId: editingNote.fieldId,
              resolvedFieldId: officeNotesFieldId,
            }
          );
          toast({
            title: 'Error',
            description:
              'Field ID is missing. Please refresh and try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const userInfo = getUserInfoFromToken();
        const newCommentLogEntry = userInfo
          ? createCommentLogEntry(
              'submitted_for_approval',
              {
                userId: userInfo.id,
                userName: userInfo.name,
                userEmail: userInfo.email,
                permissionGroup: userInfo.permissionGroup,
              },
              comment.trim() || undefined
            )
          : null;

        // Prepare transfer outcomes with alwaysTransfer set to true when submitting for approval
        const transferOutcomesToSubmit = {
          ...transferOutcomes,
          alwaysTransfer: true, // Set to true when "Submit for Approval" is clicked
        };

        const updatedNote: CustomFieldValue = {
          ...editingNote,
          metadata: {
            ...editingNote.metadata,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            transferOutcomes: transferOutcomesToSubmit as any,
            commentLog: newCommentLogEntry
              ? [...commentLog, newCommentLogEntry]
              : commentLog,
            approvalStatus: 'pending',
            submittedForApproval: true,
            submittedAt: new Date().toISOString(),
            submittedBy: '', // Backend will populate this
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        };

        const updatedNotes = notes.map((note) =>
          note.id === editingNote.id ? updatedNote : note
        );

        await saveNotes(updatedNotes, false);

        // Send notification to CHS users
        // Backend expects alertId for both alerts and notes
        await notifyCHSOnSubmission({
          alertId: editingNote.id,
          entityType,
          entityId,
          link: generateAlertLink(
            entityType,
            entityId,
            editingNote.id
          ),
          entityName: getEntityName(),
        });

        toast({
          title: 'Submitted for Approval',
          description:
            'Transfer outcomes have been submitted for CHS approval. CHS PAC managers will be notified and can review your submission.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        onClose();

        setEditingNote(updatedNote);
      }
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      toast({
        title: 'Failed to Submit for Approval',
        description:
          'An error occurred while submitting for approval. Please check your connection and try again. If the problem persists, contact support.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingForApproval(false);
    }
  };

  const handleApproveTransferOutcomes = async () => {
    if (!editingNote) {
      toast({
        title: 'No Note Selected',
        description:
          'No note is currently selected for approval. Please select a note first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!editingNote.id) {
      toast({
        title: 'Save Required First',
        description:
          'Please save the note first before approving transfer outcomes.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const editingMetadata =
      (editingNote.metadata as
        | TransferOutcomesMetadata
        | undefined) || undefined;

    if (!editingMetadata?.submittedForApproval) {
      toast({
        title: 'Submit for Approval First',
        description:
          'Submit the transfer outcomes for approval before CHS can approve them.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Use fieldId from the note itself; if missing, try refresh before failing
    const noteFieldId = await ensureOfficeNotesFieldId(editingNote);
    if (!noteFieldId) {
      console.error(
        'Field ID is missing. Cannot approve transfer outcomes.',
        {
          editingNote: editingNote.id,
          noteFieldId: editingNote.fieldId,
          resolvedFieldId: officeNotesFieldId,
        }
      );
      toast({
        title: 'Error',
        description:
          'Field ID is missing. Please refresh and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsApproving(true);

    try {
      console.log('Approving transfer outcomes:', {
        editingNote: editingNote?.id,
        fieldId: noteFieldId,
        transferOutcomes,
        commentLog: commentLog.length,
      });

      const entityId = provider ? provider.id : practice?.id || '';
      const entityType = provider ? 'provider' : 'practice';

      // Update transfer outcomes: set alwaysTransfer to false and approval status to approved
      const approvedTransferOutcomes = {
        ...transferOutcomes,
        alwaysTransfer: false, // Unset when "Approve Transfer Outcomes" is clicked
      };

      const userInfo = getUserInfoFromToken();
      const newCommentLogEntry = userInfo
        ? createCommentLogEntry(
            'approved',
            {
              userId: userInfo.id,
              userName: userInfo.name,
              userEmail: userInfo.email,
              permissionGroup: userInfo.permissionGroup,
            },
            comment.trim() || undefined
          )
        : null;

      const updatedNote: CustomFieldValue = {
        ...editingNote,
        metadata: {
          ...editingNote.metadata,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          transferOutcomes: approvedTransferOutcomes as any,
          commentLog: newCommentLogEntry
            ? [...commentLog, newCommentLogEntry]
            : commentLog,
          approvalStatus: 'approved',
          approvedAt: new Date().toISOString(),
          submittedForApproval: false,
          // Store full user info object in metadata (for comment log and audit trail)
          approvedBy: userInfo
            ? {
                userId: userInfo.id,
                userName: userInfo.name,
                userEmail: userInfo.email,
                permissionGroup: userInfo.permissionGroup,
              }
            : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      };

      const updatedNotes = notes.map((note) =>
        note.id === editingNote.id ? updatedNote : note
      );

      await saveNotes(updatedNotes, false);

      // Send notification to Denim users
      // Backend expects alertId for both alerts and notes
      await notifyDenimOnApproval({
        alertId: editingNote.id,
        entityType,
        entityId,
        link: generateAlertLink(entityType, entityId, editingNote.id),
        entityName: getEntityName(),
      });

      // Clear comment after approval
      setComment('');

      setEditingNote(updatedNote);

      toast({
        title: 'Transfer Outcomes Approved',
        description:
          'Transfer outcomes have been approved and are now live. Denim users have been notified of the approval.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Failed to approve transfer outcomes:', error);
      toast({
        title: 'Failed to Approve Transfer Outcomes',
        description:
          'An error occurred while approving transfer outcomes. Please check your connection and try again. If the problem persists, contact support.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    // Set endDate to day before yesterday for proper deletion across all timezones
    const dayBeforeYesterday = new Date();
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
    // Format date safely to avoid timezone issues
    const year = dayBeforeYesterday.getFullYear();
    const month = String(dayBeforeYesterday.getMonth() + 1).padStart(
      2,
      '0'
    );
    const day = String(dayBeforeYesterday.getDate()).padStart(2, '0');
    const ymd = `${year}-${month}-${day}`;

    const updatedNotes = notes.map((note) =>
      note.id === noteToDelete.id
        ? {
            ...note,
            endDate: ymd,
          }
        : note
    );

    try {
      setIsDeleting(true);
      await saveNotes(updatedNotes, false); // Don't show success toast from saveNotes
      analytics.trackEvent('delete', 'office_alert');

      // Only update UI after successful API call - directly remove the deleted note
      setNotes((prevNotes) =>
        prevNotes.filter((note) => note.id !== noteToDelete?.id)
      );

      // Show success feedback only after successful API call
      toast({
        title: 'Note Deleted',
        description: 'Office note has been deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Failed to delete note',
        description:
          'An error occurred while deleting the note. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onDeleteClose();
    }
  };

  const saveNotes = async (
    updatedNotes: CustomFieldValue[],
    showSuccessToast: boolean = true
  ): Promise<CustomFieldValue[]> => {
    try {
      setIsUpdating(true);

      // Filter out notes with empty or whitespace-only values
      const validNotes = updatedNotes.filter(
        (note) => note.value && note.value.trim().length > 0
      );

      if (provider) {
        const cleanedProvider: Partial<Provider> = {
          id: provider.id,
          customFields: {
            'Office-Notes': validNotes.map((note) => ({
              ...note,
              id: note.id ?? undefined, // Ensure id is included for edited notes
            })),
          },
        };

        await updateProviderById(cleanedProvider.id, cleanedProvider);

        // Re-fetch provider to get updated notes with IDs from backend
        const { getProviderById } = await import(
          '../../services/providers/getProviderById'
        );
        const { data } = await getProviderById(provider.id);
        const savedNotes =
          data?.provider?.customFields?.['Office-Notes'] || [];
        const notesWithIds = Array.isArray(savedNotes)
          ? savedNotes
          : [];

        // Update local state with notes that have IDs from backend
        setNotes(notesWithIds);

        // Show success feedback only if requested (not for deletions)
        if (showSuccessToast) {
          toast({
            title: 'Note Saved',
            description: 'Office note saved successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }

        return notesWithIds;
      } else if (practice) {
        const cleanedPractice: Partial<Practice> = {
          id: practice.id,
          customFields: {
            'Office-Notes': validNotes.map((note) => ({
              ...note,
              id: note.id ?? undefined, // Ensure id is included for edited notes
            })),
          } as Practice['customFields'],
        };

        await updatePracticeById(cleanedPractice.id, cleanedPractice);

        // Re-fetch practice to get updated notes with IDs from backend
        const { getPracticeById } = await import(
          '../../services/practices/getPracticeById'
        );
        const { data } = await getPracticeById(practice.id);
        const savedNotes =
          data?.practice?.customFields?.['Office-Notes'] || [];
        const notesWithIds = Array.isArray(savedNotes)
          ? savedNotes
          : [];

        // Update local state with notes that have IDs from backend
        setNotes(notesWithIds);

        // Show success feedback only if requested (not for deletions)
        if (showSuccessToast) {
          toast({
            title: 'Note Saved',
            description: 'Office note saved successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }

        return notesWithIds;
      }

      // Don't close modal here - let handleSaveNote handle it
      return validNotes;
    } catch (error) {
      const isProvider = !!provider;
      const errorTitle = isProvider
        ? 'Failed to update provider notes.'
        : 'Failed to update office notes.';

      // Only show error toast if showSuccessToast is true (for regular saves)
      // For deletions, let the calling function handle the error
      if (showSuccessToast) {
        toast({
          title: errorTitle,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }

      // Always re-throw the error so calling functions can handle it
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  // Derive visible list defensively to avoid empty-card renders
  const visibleNotes = Array.isArray(notes)
    ? notes.filter((n) => !!n && n.value && n.value.trim())
    : [];

  // Sort notes by start date (most recent first)
  const sortedNotes = visibleNotes.sort(
    (a, b) =>
      new Date(b.startDate || '').getTime() -
      new Date(a.startDate || '').getTime()
  );

  const hasNotes = sortedNotes.length > 0;
  const latestNote = hasNotes ? sortedNotes[0] : undefined;
  const olderNotes = hasNotes ? sortedNotes.slice(1) : [];

  const submittedForApproval = Boolean(
    (editingNote?.metadata as TransferOutcomesMetadata | undefined)
      ?.submittedForApproval
  );

  const approveDisabledReason = submittedForApproval
    ? undefined
    : 'Submit for approval before CHS can approve transfer outcomes.';

  return (
    <Box
      padding="0.5rem 0.625rem"
      backgroundColor="background.blue100"
      borderRadius="0.5rem"
      width={width ? width : '100%'}
      mt="0.25rem"
    >
      {isFieldIdLoading ? (
        <Flex align="center" justify="center">
          <Spinner size="md" color="yellow.800" />
          <Text ml="1rem">
            {provider
              ? 'Loading Provider Notes...'
              : 'Loading Office Notes...'}
          </Text>
        </Flex>
      ) : (
        <Stack spacing={4}>
          {hasNotes && latestNote && (
            <Box
              key={latestNote.id}
              padding="1rem"
              backgroundColor="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius="0.75rem"
              display="flex"
              flexDirection="column"
              gap="0.75rem"
              minWidth={0}
              overflow="hidden"
              _hover={{
                boxShadow: 'md',
                borderColor: 'blue.300',
              }}
              transition="all 0.2s ease-in-out"
            >
              {/* Header Row */}
              <Flex
                justify="flex-start"
                align="center"
                flexWrap="wrap"
                gap={2}
              >
                <HStack spacing={3} align="center">
                  <img
                    src={OfficeNotesIcon}
                    alt={
                      provider
                        ? 'Provider Notes Icon'
                        : 'Office Notes Icon'
                    }
                    style={{ width: '20px', height: '20px' }}
                  />
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.800"
                  >
                    {provider
                      ? 'Provider Note'
                      : 'Practice Office Note'}
                  </Text>
                </HStack>

                {/* Actions - Will wrap to next line if needed */}
                <HStack
                  spacing={2}
                  minW="5rem"
                  justify="flex-end"
                  ml="auto"
                >
                  {canEditDailyNotes() ||
                  canEditOfficeAlerts() ||
                  isAdmin() ? (
                    <>
                      <IconButton
                        aria-label="Edit Note"
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() => handleEditNote(latestNote)}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                      />
                      <IconButton
                        aria-label="Delete Note"
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => {
                          setNoteToDelete(latestNote);
                          onDeleteOpen();
                        }}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                        isDisabled={isDeleting}
                      />
                    </>
                  ) : null}
                </HStack>
              </Flex>

              {/* Practice Association */}
              {latestNote.metadata?.relatedPracticeId &&
                (() => {
                  const practiceId = extractPracticeId(
                    latestNote?.metadata?.relatedPracticeId
                  );
                  // Use relatedPracticeName from metadata if available, otherwise fall back to getPracticeNameById
                  const practiceName = String(
                    latestNote.metadata?.relatedPracticeName ||
                      (practiceId
                        ? getPracticeNameById(practiceId)
                        : '') ||
                      ''
                  );

                  // Show loading state while practices are being fetched
                  if (isPracticesLoading) {
                    return (
                      <Box>
                        <Text
                          fontSize="0.875rem"
                          color="gray.500"
                          fontWeight="500"
                        >
                          Related to:{' '}
                          <HStack
                            spacing={2}
                            align="center"
                            display="inline-flex"
                          >
                            <Spinner size="xs" color="gray.400" />
                            <Text>Loading practice...</Text>
                          </HStack>
                        </Text>
                      </Box>
                    );
                  }

                  // Only show if we have a valid practice name (not "Unknown Practice")
                  if (
                    !practiceId ||
                    practiceName === 'Unknown Practice'
                  ) {
                    return null;
                  }

                  return (
                    <Box>
                      <Text
                        fontSize="0.875rem"
                        color="blue.600"
                        fontWeight="500"
                      >
                        Related to:{' '}
                        <span
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (practiceId) {
                              navigate(`/practices/${practiceId}`);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={`Go to practice ${practiceName}`}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              if (practiceId) {
                                navigate(`/practices/${practiceId}`);
                              }
                            }
                          }}
                        >
                          {practiceName}
                        </span>
                      </Text>
                    </Box>
                  );
                })()}

              {/* Categories */}
              {latestNote.metadata?.categories &&
                Array.isArray(latestNote.metadata.categories) &&
                latestNote.metadata.categories.length > 0 && (
                  <Flex gap={2} flexWrap="wrap">
                    {latestNote.metadata.categories
                      .filter(
                        (cat): cat is string =>
                          typeof cat === 'string'
                      )
                      .map((category, index) => (
                        <Tooltip
                          key={index}
                          label={
                            CATEGORY_LEGEND[
                              category as keyof typeof CATEGORY_LEGEND
                            ]?.definition || category
                          }
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                          maxW="300px"
                        >
                          <Badge
                            colorScheme={getCategoryColor(category)}
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            {CATEGORY_LEGEND[
                              category as keyof typeof CATEGORY_LEGEND
                            ]?.name || category.replace(/_/g, ' ')}
                          </Badge>
                        </Tooltip>
                      ))}
                  </Flex>
                )}

              {/* Transfer Outcomes Status Badges - Only visible to users with permissions */}
              {(canEditDailyNotes() ||
                canEditOfficeAlerts() ||
                isAdmin()) &&
                (() => {
                  const transferOutcomesData = getTransferOutcomes(
                    latestNote.metadata
                  );
                  const approvalStatus =
                    latestNote.metadata?.approvalStatus;
                  const alwaysTransfer =
                    transferOutcomesData?.alwaysTransfer;

                  if (!transferOutcomesData && !approvalStatus) {
                    return null;
                  }

                  return (
                    <Flex gap={2} flexWrap="wrap" alignItems="center">
                      {/* Approval Status Badge */}
                      {approvalStatus && (
                        <Tooltip
                          label={
                            approvalStatus === 'pending'
                              ? 'Awaiting CHS approval'
                              : approvalStatus === 'approved'
                              ? 'Transfer outcomes approved and live'
                              : 'Draft - not yet submitted'
                          }
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Badge
                            colorScheme={
                              approvalStatus === 'approved'
                                ? 'green'
                                : approvalStatus === 'pending'
                                ? 'orange'
                                : 'gray'
                            }
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            {approvalStatus === 'approved'
                              ? 'Approved'
                              : approvalStatus === 'pending'
                              ? 'Pending Approval'
                              : 'Draft'}
                          </Badge>
                        </Tooltip>
                      )}

                      {/* Always Transfer Badge - Only show if true */}
                      {alwaysTransfer === true && (
                        <Tooltip
                          label="Bot will always transfer until outcomes are approved"
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Badge
                            colorScheme="yellow"
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            Always Transfer
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                  );
                })()}

              {/* Note Preview */}
              <NotePreview
                content={
                  typeof latestNote.metadata?.htmlContent === 'string'
                    ? latestNote.metadata.htmlContent
                    : latestNote.value
                }
              />
            </Box>
          )}

          {olderNotes.length > 0 && (
            <Collapse in={!isCollapsed}>
              {olderNotes.map((note) => (
                <Box
                  key={note.id}
                  padding="1rem"
                  backgroundColor="blue.50"
                  border="1px solid"
                  borderColor="blue.200"
                  borderRadius="0.75rem"
                  display="flex"
                  flexDirection="column"
                  gap="0.75rem"
                  mt="0.75rem"
                  minWidth={0}
                  overflow="hidden"
                  _hover={{
                    boxShadow: 'md',
                    borderColor: 'blue.300',
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  {/* Header Row */}
                  <Flex
                    justify="flex-start"
                    align="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <HStack spacing={3} align="center">
                      <img
                        src={OfficeNotesIcon}
                        alt={
                          provider
                            ? 'Provider Notes Icon'
                            : 'Office Notes Icon'
                        }
                        style={{ width: '20px', height: '20px' }}
                      />
                      <Text
                        fontSize="1rem"
                        fontWeight="600"
                        color="gray.800"
                      >
                        {provider
                          ? 'Provider Note'
                          : 'Practice Office Note'}
                      </Text>
                    </HStack>

                    {/* Actions - Will wrap to next line if needed */}
                    <HStack
                      spacing={2}
                      minW="5rem"
                      justify="flex-end"
                      ml="auto"
                    >
                      {canEditDailyNotes() ||
                      canEditOfficeAlerts() ||
                      isAdmin() ? (
                        <>
                          <IconButton
                            aria-label="Edit Note"
                            icon={<EditIcon />}
                            size="sm"
                            onClick={() => handleEditNote(note)}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                          />
                          <IconButton
                            aria-label="Delete Note"
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => {
                              setNoteToDelete(note);
                              onDeleteOpen();
                            }}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                            isDisabled={isDeleting}
                          />
                        </>
                      ) : null}
                    </HStack>
                  </Flex>

                  {/* Practice Association */}
                  {note.metadata?.relatedPracticeId &&
                    (() => {
                      const idRaw = Array.isArray(
                        note.metadata.relatedPracticeId
                      )
                        ? note.metadata.relatedPracticeId[0]
                        : note.metadata.relatedPracticeId;
                      let practiceId = '';
                      if (
                        idRaw &&
                        typeof idRaw === 'object' &&
                        'providerId' in idRaw
                      ) {
                        practiceId = (idRaw as { providerId: string })
                          .providerId;
                      } else if (typeof idRaw === 'string') {
                        practiceId = idRaw;
                      }

                      // Use relatedPracticeName from metadata if available, otherwise fall back to getPracticeNameById
                      const practiceName = String(
                        note.metadata?.relatedPracticeName ||
                          (practiceId
                            ? getPracticeNameById(practiceId)
                            : '') ||
                          ''
                      );

                      // Show loading state while practices are being fetched
                      if (isPracticesLoading) {
                        return (
                          <Box>
                            <Text
                              fontSize="0.875rem"
                              color="gray.500"
                              fontWeight="500"
                            >
                              Related to:{' '}
                              <HStack
                                spacing={2}
                                align="center"
                                display="inline-flex"
                              >
                                <Spinner size="xs" color="gray.400" />
                                <Text>Loading practice...</Text>
                              </HStack>
                            </Text>
                          </Box>
                        );
                      }

                      // Only show if we have a valid practice name (not "Unknown Practice")
                      if (
                        !practiceId ||
                        practiceName === 'Unknown Practice'
                      ) {
                        return null;
                      }

                      return (
                        <Box>
                          <Text
                            fontSize="0.875rem"
                            color="blue.600"
                            fontWeight="500"
                          >
                            Related to:{' '}
                            <span
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                navigate(`/practices/${practiceId}`)
                              }
                            >
                              {practiceName}
                            </span>
                          </Text>
                        </Box>
                      );
                    })()}

                  {/* Categories */}
                  {note.metadata?.categories &&
                    Array.isArray(note.metadata.categories) &&
                    note.metadata.categories.length > 0 && (
                      <Flex gap={2} flexWrap="wrap">
                        {note.metadata.categories
                          .filter(
                            (cat): cat is string =>
                              typeof cat === 'string'
                          )
                          .map((category, index) => (
                            <Tooltip
                              key={index}
                              label={
                                CATEGORY_LEGEND[
                                  category as keyof typeof CATEGORY_LEGEND
                                ]?.definition || category
                              }
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              maxW="300px"
                            >
                              <Badge
                                colorScheme={getCategoryColor(
                                  category
                                )}
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                {CATEGORY_LEGEND[
                                  category as keyof typeof CATEGORY_LEGEND
                                ]?.name ||
                                  category.replace(/_/g, ' ')}
                              </Badge>
                            </Tooltip>
                          ))}
                      </Flex>
                    )}

                  {/* Transfer Outcomes Status Badges - Only visible to users with permissions */}
                  {(canEditDailyNotes() ||
                    canEditOfficeAlerts() ||
                    isAdmin()) &&
                    (() => {
                      const transferOutcomesData =
                        getTransferOutcomes(note.metadata);
                      const approvalStatus =
                        note.metadata?.approvalStatus;
                      const alwaysTransfer =
                        transferOutcomesData?.alwaysTransfer;

                      if (!transferOutcomesData && !approvalStatus) {
                        return null;
                      }

                      return (
                        <Flex
                          gap={2}
                          flexWrap="wrap"
                          alignItems="center"
                        >
                          {/* Approval Status Badge */}
                          {approvalStatus && (
                            <Tooltip
                              label={
                                approvalStatus === 'pending'
                                  ? 'Awaiting CHS approval'
                                  : approvalStatus === 'approved'
                                  ? 'Transfer outcomes approved and live'
                                  : 'Draft - not yet submitted'
                              }
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Badge
                                colorScheme={
                                  approvalStatus === 'approved'
                                    ? 'green'
                                    : approvalStatus === 'pending'
                                    ? 'orange'
                                    : 'gray'
                                }
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                {approvalStatus === 'approved'
                                  ? 'Approved'
                                  : approvalStatus === 'pending'
                                  ? 'Pending Approval'
                                  : 'Draft'}
                              </Badge>
                            </Tooltip>
                          )}

                          {/* Always Transfer Badge - Only show if true */}
                          {alwaysTransfer === true && (
                            <Tooltip
                              label="Bot will always transfer until outcomes are approved"
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Badge
                                colorScheme="yellow"
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                Always Transfer
                              </Badge>
                            </Tooltip>
                          )}
                        </Flex>
                      );
                    })()}

                  {/* Note Preview */}
                  <NotePreview
                    content={
                      typeof note.metadata?.htmlContent === 'string'
                        ? note.metadata.htmlContent
                        : note.value
                    }
                  />
                </Box>
              ))}
            </Collapse>
          )}

          {/* Add Note and Show More buttons */}
          <Flex
            justify="flex-start"
            align="center"
            flexWrap="wrap"
            gap={2}
          >
            {/* Show "No Data" message when no notes exist */}
            {!hasNotes && (
              <Text
                fontSize="0.875rem"
                color="gray.500"
                fontStyle="italic"
                textAlign="center"
                width="100%"
                py="1rem"
              >
                {practice
                  ? 'No Practice Office Notes'
                  : 'No Provider Notes'}
              </Text>
            )}

            {/* Always show Add button for users with permissions */}
            {(canEditDailyNotes() ||
              canEditOfficeAlerts() ||
              isAdmin()) && (
              <>
                {passedCustomFieldsError ? (
                  <Flex align="center" gap={2} px={4}>
                    <Text fontSize="0.875rem" color="red.500" fontWeight="500">
                      Failed to initialize Notes.
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => refreshCustomFields?.()}
                    >
                      Retry
                    </Button>
                  </Flex>
                ) : passedCustomFieldsLoading || (passedCustomFields && !officeNotesFieldId) ? (
                  <Flex align="center" gap={2} px={4}>
                    <Spinner size="sm" color="blue.500" />
                    <Text fontSize="0.875rem" color="gray.600">
                      Initializing Notes...
                    </Text>
                  </Flex>
                ) : (
                  <Button
                    size="sm"
                    leftIcon={<AddIcon />}
                    onClick={handleAddNewNote}
                    backgroundColor="background.blue100"
                    color="black"
                    _hover={{ backgroundColor: 'blue.200' }}
                  >
                    Add{' '}
                    {practice ? 'Practice Office Note' : 'Provider Note'}
                  </Button>
                )}
              </>
            )}

            {/* Show More/Less button when there are older notes */}
            {hasNotes && olderNotes.length > 0 && (
              <Button
                size="sm"
                leftIcon={
                  isCollapsed ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronUpIcon />
                  )
                }
                onClick={() => setIsCollapsed(!isCollapsed)}
                backgroundColor="background.blue100"
                color="black"
                _hover={{ backgroundColor: 'blue.200' }}
              >
                {isCollapsed
                  ? `Show ${olderNotes.length} more`
                  : 'Hide older notes'}
              </Button>
            )}
          </Flex>
        </Stack>
      )}

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Note
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this note? This action
              cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteNote}
                ml={3}
                isLoading={isUpdating}
                loadingText="Deleting..."
                isDisabled={isUpdating}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent
          maxW="90vw"
          maxH="85vh"
          mx="auto"
          borderRadius="xl"
          boxShadow="2xl"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="blue.50"
            borderTopRadius="xl"
          >
            <Text fontSize="1.25rem" fontWeight="600">
              {practice ? 'Practice Office Notes' : 'Provider Notes'}
            </Text>
          </ModalHeader>

          <ModalBody
            p="2rem"
            flex="1"
            overflowY="auto"
            minH="0"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e0',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0aec0',
              },
            }}
          >
            <VStack spacing="1.5rem" align="stretch">
              {/* Categories Multiselect - Commented out for now, may be needed in future */}
              {/* <Box>
                <HStack spacing={2} align="center" mb="0.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Select Category
                  </Text>
                  <IconButton
                    aria-label="Category Legend"
                    icon={<InfoIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={onCategoryLegendOpen}
                    color="blue.500"
                    h="1.5rem"
                    w="1.5rem"
                    borderRadius="0.375rem"
                    _hover={{
                      bg: 'blue.50',
                      color: 'blue.600',
                    }}
                  />
                </HStack>
                <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
                  Choose one or more categories that apply to this
                  note.
                </Text>
                {categoryOptions.length > 0 ? (
                  <Box>
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        width="100%"
                        textAlign="left"
                        fontWeight="normal"
                        rightIcon={<ChevronDownIcon />}
                      >
                        {selectedCategories.length > 0
                          ? `${selectedCategories.length} selected`
                          : 'Select categories...'}
                      </MenuButton>
                      <MenuList maxH="300px" overflowY="auto">
                        {categoryOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            onClick={() => {
                              setSelectedCategories((prev) =>
                                prev.includes(option.value)
                                  ? prev.filter(
                                      (v) => v !== option.value
                                    )
                                  : [...prev, option.value]
                              );
                            }}
                          >
                            <Checkbox
                              isChecked={selectedCategories.includes(
                                option.value
                              )}
                              mr={2}
                              pointerEvents="none"
                            />
                            <Badge
                              colorScheme={getCategoryColor(
                                option.value
                              )}
                              fontSize="xs"
                            >
                              {option.label}
                            </Badge>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                    {selectedCategories.length > 0 && (
                      <Wrap mt={2} spacing={2}>
                        {selectedCategories.map((category) => (
                          <WrapItem key={category}>
                            <Tooltip
                              label={CATEGORY_LEGEND[category as keyof typeof CATEGORY_LEGEND]?.definition || category}
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              maxW="300px"
                            >
                              <Tag
                                size="md"
                                colorScheme={getCategoryColor(category)}
                                borderRadius="md"
                                cursor="help"
                              >
                                <TagLabel>
                                  {CATEGORY_LEGEND[category as keyof typeof CATEGORY_LEGEND]?.name || category.replace(/_/g, ' ')}
                                </TagLabel>
                                <TagCloseButton
                                  onClick={() =>
                                    setSelectedCategories((prev) =>
                                      prev.filter((c) => c !== category)
                                    )
                                  }
                                  aria-label={`Remove ${category}`}
                                />
                              </Tag>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </Box>
                ) : (
                  <Text fontSize="0.875rem" color="orange.600">
                    Category options are not available. You can still
                    save the note.
                  </Text>
                )}
              </Box> */}

              <Box>
                <Text
                  mb="0.5rem"
                  fontSize="0.875rem"
                  fontWeight="500"
                  color="gray.700"
                >
                  Note Message <span style={{ color: 'red' }}>*</span>
                </Text>
                <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
                  Enter a clear, actionable note message. Rich text
                  formatting is supported.
                </Text>
                <RichTextEditor
                  value={tempNote}
                  onChange={setTempNote}
                  placeholder={
                    provider
                      ? 'Enter provider note message...'
                      : 'Enter office note message...'
                  }
                  minHeight="4rem"
                  maxHeight="20rem"
                />
              </Box>

              <HStack align="flex-end" spacing="1rem">
                <Box flex="1">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                    mb="0.5rem"
                  >
                    Start Date <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    When this note begins. Can be in the past.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={startDate || ''}
                      onChange={(e) => setStartDate(e.target.value)}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        // Format date safely to avoid timezone issues
                        const year = tomorrow.getFullYear();
                        const month = String(
                          tomorrow.getMonth() + 1
                        ).padStart(2, '0');
                        const day = String(
                          tomorrow.getDate()
                        ).padStart(2, '0');
                        setStartDate(`${year}-${month}-${day}`);
                      }}
                      title="Set start date to tomorrow"
                    >
                      Tomorrow
                    </Button>
                  </HStack>
                </Box>
                <Box flex="1">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                    mb="0.5rem"
                  >
                    End Date
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    When this note ends. Must be after the Start Date.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={neverExpire ? '' : endDate || ''}
                      onChange={(e) => setEndDate(e.target.value)}
                      isDisabled={neverExpire}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (startDate) {
                          const startDateObj = new Date(startDate);
                          const weekLater = new Date(startDateObj);
                          weekLater.setDate(weekLater.getDate() + 7);
                          // Format date safely to avoid timezone issues
                          const year = weekLater.getFullYear();
                          const month = String(
                            weekLater.getMonth() + 1
                          ).padStart(2, '0');
                          const day = String(
                            weekLater.getDate()
                          ).padStart(2, '0');
                          setEndDate(`${year}-${month}-${day}`);
                        }
                      }}
                      isDisabled={!startDate || neverExpire}
                      title="Set end date to 1 week from start date"
                    >
                      +1 Week
                    </Button>
                  </HStack>
                </Box>
              </HStack>

              <Flex align="center" mt="0.5rem">
                <input
                  type="checkbox"
                  id="never-expire"
                  checked={neverExpire}
                  onChange={(e) => {
                    setNeverExpire(e.target.checked);
                    if (e.target.checked) setEndDate(null);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                <label
                  htmlFor="never-expire"
                  style={{ fontSize: '0.875rem', color: 'gray.700' }}
                >
                  Never Expire
                </label>
                <Text fontSize="0.75rem" color="gray.600" ml="0.5rem">
                  Check this if the note should never automatically
                  expire
                </Text>
              </Flex>

              {provider && (
                <Flex align="center" mt="0.5rem">
                  <input
                    type="checkbox"
                    id="associate-practice"
                    checked={associateWithPractice}
                    onChange={(e) =>
                      setAssociateWithPractice(e.target.checked)
                    }
                    style={{ marginRight: '0.5rem' }}
                  />
                  <label
                    htmlFor="associate-practice"
                    style={{
                      fontSize: '0.875rem',
                      color: 'gray.700',
                    }}
                  >
                    Associate with Practice
                  </label>
                </Flex>
              )}

              {associateWithPractice && (
                <Box>
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                    mb="0.5rem"
                  >
                    Select Practice
                  </Text>
                  <select
                    value={selectedPracticeId}
                    onChange={(e) =>
                      setSelectedPracticeId(e.target.value)
                    }
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'white',
                      fontSize: '0.875rem',
                    }}
                  >
                    <option value="">Select a practice</option>
                    {/* Use provider.relatedPractices if available, otherwise fall back to practices prop */}
                    {(provider?.relatedPractices &&
                    provider.relatedPractices.length > 0
                      ? provider.relatedPractices
                      : practices || []
                    ).map((relatedPractice) => (
                      <option
                        key={relatedPractice.id}
                        value={relatedPractice.id}
                      >
                        {relatedPractice.name}
                      </option>
                    ))}
                  </select>
                </Box>
              )}

              {/* Transfer Outcomes Configuration */}
              {(canEditDailyNotes() ||
                canEditOfficeAlerts() ||
                isAdmin()) &&
                (isSpecialtyOptionsLoading ||
                localSpecialtyLoading ? (
                  <Box
                    p="2rem"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Spinner size="md" color="blue.500" mb="1rem" />
                    <Text fontSize="0.95rem" color="gray.700">
                      Loading specialty options…
                    </Text>
                    <Text
                      fontSize="0.875rem"
                      color="gray.500"
                      mt="0.5rem"
                    >
                      Please wait while we load the available
                      specialties.
                    </Text>
                  </Box>
                ) : (
                  <TransferOutcomesConfig
                    isPracticeLevel={!!practice}
                    transferOutcomes={transferOutcomes}
                    comment={comment}
                    commentLog={commentLog}
                    onTransferOutcomesChange={setTransferOutcomes}
                    onCommentChange={setComment}
                    onAddComment={handleAddComment}
                    isAddingComment={isAddingComment}
                    showSubmitForApproval={
                      canEditDailyNotes() ||
                      canEditOfficeAlerts() ||
                      isAdmin()
                      // Visible to admins and alert editors
                    }
                    hasExistingId={!!editingNote?.id}
                    showApproveButton={isAdmin()}
                    canApprove={submittedForApproval}
                    approveDisabledReason={approveDisabledReason}
                    onSubmitForApproval={handleSubmitForApproval}
                    onApprove={handleApproveTransferOutcomes}
                    isSubmitting={isSubmittingForApproval}
                    isApproving={isApproving}
                    specialtyOptions={specialtyOptions}
                  />
                ))}
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1.5rem"
            pb="1.5rem"
            px="2rem"
            bg="blue.50"
            borderBottomRadius="xl"
            flexShrink={0}
            boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1)"
          >
            <HStack spacing="1rem" width="100%" justify="flex-end">
              <Button
                variant="outline"
                onClick={onClose}
                size="md"
                borderRadius="md"
                minW="100px"
                isDisabled={
                  isUpdating || isSubmittingForApproval || isApproving
                }
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSaveNote}
                isLoading={isUpdating}
                loadingText={
                  editingNote ? 'Updating...' : 'Saving...'
                }
                size="md"
                borderRadius="md"
                minW="120px"
                isDisabled={
                  isUpdating || isSubmittingForApproval || isApproving
                }
              >
                {editingNote ? 'Update Note' : 'Save Note'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Category Legend Modal */}
      <Modal
        isOpen={isCategoryLegendOpen}
        onClose={onCategoryLegendClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>Category Legend & Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb="2rem"
            maxH="calc(85vh - 120px)"
            overflowY="auto"
          >
            <Stack spacing="1rem">
              {Object.entries(CATEGORY_LEGEND).map(
                ([key, { name, definition }]) => (
                  <Flex
                    key={key}
                    gap="1rem"
                    alignItems="flex-start"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    pb="1rem"
                    _last={{ borderBottom: 'none', pb: 0 }}
                  >
                    <Badge
                      colorScheme={getCategoryColor(key)}
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="md"
                      flexShrink={0}
                      minW="120px"
                    >
                      {name}
                    </Badge>
                    <Text
                      fontSize="0.875rem"
                      color="gray.700"
                      flex="1"
                    >
                      {definition}
                    </Text>
                  </Flex>
                )
              )}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OfficeNotesCard;
